import React from 'react';
import { Property } from '@/types/property';
import { GridFSImage } from './GridFSImage';
import { cn, formatPrice } from '@/lib/utils';
import { Check, X, Building, Home, MapPin, Building2 } from 'lucide-react';

interface PropertyComparisonTableProps {
  properties: Property[];
}

export function PropertyComparisonTable({ properties }: PropertyComparisonTableProps) {
  if (properties.length === 0) return null;

  const COMPARISON_FIELDS = [
    { key: "price", label: "Precio", format: "currency" },
    { key: "area", label: "Superficie", format: "m2" },
    { key: "price_per_m2", label: "Precio/m²", format: "currency", calculated: true },
    { key: "bedrooms", label: "Habitaciones", format: "number" },
    { key: "bathrooms", label: "Baños", format: "number" },
    { key: "parking", label: "Estacionamientos", format: "number" },
    { key: "yearBuilt", label: "Año Construcción", format: "year" },
    { key: "propertyType", label: "Tipo", format: "text" },
    { key: "location", label: "Ubicación", format: "location" },
    { key: "condition", label: "Estado", format: "text" },
  ];

  const getBestValue = (key: string, values: any[]) => {
    const validValues = values.filter(v => v !== null && v !== undefined);
    if (validValues.length <= 1) return null;

    switch (key) {
      case "price":
      case "price_per_m2":
        return Math.min(...validValues);
      case "area":
      case "bedrooms":
      case "bathrooms":
      case "parking":
      case "yearBuilt":
        return Math.max(...validValues);
      default:
        return null;
    }
  };

  const renderValue = (field: any, property: Property) => {
    let value: any;
    if (field.calculated && field.key === 'price_per_m2') {
      value = property.price / property.area;
    } else if (field.key === 'location') {
      value = `${property.location?.city || ''}, ${property.location?.state || ''}`;
    } else {
      value = (property as any)[field.key];
    }

    const allValues = properties.map(p => {
      if (field.calculated && field.key === 'price_per_m2') return p.price / p.area;
      if (field.key === 'location') return p.location?.city;
      return (p as any)[field.key];
    });

    const isBest = value !== null && value !== undefined && value === getBestValue(field.key, allValues);

    switch (field.format) {
      case "currency":
        return (
          <div className={cn("text-lg font-display font-black", isBest && "text-terracotta")}>
            ${formatPrice(value || 0)}
          </div>
        );
      case "m2":
        return (
          <div className={cn("text-base font-black", isBest && "text-terracotta")}>
            {value || 0} m²
          </div>
        );
      case "number":
      case "year":
        return (
          <div className={cn("text-base font-black", isBest && "text-terracotta")}>
            {value || 'N/A'}
          </div>
        );
      default:
        return <div className="text-sm font-serif italic text-ink/70">{value || 'N/A'}</div>;
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border-4 border-ink bg-parchment">
        <thead>
          <tr className="border-b-4 border-ink">
            <th className="p-8 text-left bg-ink text-parchment w-1/4">
              <div className="flex items-center gap-3">
                <Scale className="h-6 w-6 text-terracotta" />
                <span className="text-xs font-black uppercase tracking-[0.3em]">Características</span>
              </div>
            </th>
            {properties.map((prop) => (
              <th key={prop._id || prop.id} className="p-8 bg-parchment border-l-4 border-ink relative">
                <div className="relative aspect-[4/3] w-full mb-6 brutalist-border-sm overflow-hidden">
                  <GridFSImage 
                    src={prop.images?.[0] || ''} 
                    alt={prop.title} 
                    size="full" 
                    className="grayscale hover:grayscale-0 transition-all duration-700"
                  />
                </div>
                <h3 className="text-xl font-display font-black uppercase leading-none italic text-ink text-left line-clamp-2">
                  {prop.title}
                </h3>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARISON_FIELDS.map((field) => (
            <tr key={field.key} className="border-b-2 border-ink/10 hover:bg-ink/[0.02] transition-colors">
              <td className="p-6 bg-ink/[0.03] border-r-4 border-ink">
                <span className="text-[10px] font-black uppercase tracking-widest text-ink/40">
                  {field.label}
                </span>
              </td>
              {properties.map((prop) => (
                <td key={prop._id || prop.id} className="p-6 border-l-4 border-ink text-left">
                  {renderValue(field, prop)}
                </td>
              ))}
            </tr>
          ))}
          {/* Action Row */}
          <tr>
            <td className="p-6 bg-ink/[0.03] border-r-4 border-ink"></td>
            {properties.map((prop) => (
              <td key={prop._id || prop.id} className="p-8 border-l-4 border-ink">
                <button 
                  onClick={() => window.location.href = `/property/${prop._id || prop.id}`}
                  className="w-full bg-ink text-parchment py-4 text-xs font-black uppercase tracking-[0.2em] hover:bg-terracotta transition-all shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]"
                >
                  Ver Detalle
                </button>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

import { Scale } from 'lucide-react';
